"use strict";
// CSS text-transform property parser
// Handles parsing of CSS text-transform property according to MDN specification
// https://developer.mozilla.org/en-US/docs/Web/CSS/text-transform
Object.defineProperty(exports, "__esModule", { value: true });
exports.parse = parse;
exports.toCSSValue = toCSSValue;
const shared_utils_1 = require('../utils/shared-utils.cjs');
const css_variable_1 = require('./css-variable.cjs');
// Import and re-export centralized types
const types_1 = require('../types.cjs');
/**
 * Checks if a string is a valid text-transform keyword
 */
function isTextTransformKeyword(value) {
    return types_1.TEXT_TRANSFORM_KEYWORDS.includes(value.toLowerCase());
}
/**
 * Validates keyword combinations according to CSS specification
 */
function areKeywordsCombinationValid(keywords) {
    // All keywords must be valid text-transform keywords
    if (!keywords.every(kw => isTextTransformKeyword(kw))) {
        return false;
    }
    // 'none' cannot be combined with other keywords
    if (keywords.includes('none') && keywords.length > 1) {
        return false;
    }
    // Case transformation keywords cannot be combined
    const caseKeywords = keywords.filter(kw => ['uppercase', 'lowercase', 'capitalize'].includes(kw));
    if (caseKeywords.length > 1) {
        return false;
    }
    return true;
}
/**
 * Parses a CSS text-transform property string into structured components
 * Follows MDN specification: https://developer.mozilla.org/en-US/docs/Web/CSS/text-transform
 */
function parse(value) {
    if (!value || typeof value !== 'string') {
        return null;
    }
    const trimmed = value.trim();
    if (trimmed === '') {
        return null;
    }
    // CSS variables can be parsed directly
    if ((0, shared_utils_1.isCssVariable)(trimmed)) {
        return (0, css_variable_1.parse)(trimmed);
    }
    // Handle global keywords
    if ((0, shared_utils_1.isGlobalKeyword)(trimmed)) {
        return { type: 'keyword', keyword: trimmed.toLowerCase() };
    }
    // Handle text-transform keywords (single or multiple)
    const keywords = trimmed.toLowerCase().split(/\s+/).filter(Boolean);
    // Validate keyword combination
    if (keywords.length > 0 && areKeywordsCombinationValid(keywords)) {
        return { type: 'keyword', keyword: keywords.join(' ') };
    }
    return null;
}
/**
 * Converts a parsed text-transform back to a CSS value string
 * @param parsed - The parsed text-transform object
 * @returns CSS value string or null if invalid
 */
function toCSSValue(parsed) {
    if (!parsed) {
        return null;
    }
    // Handle CSS variables
    if ('CSSvariable' in parsed) {
        return (0, css_variable_1.toCSSValue)(parsed);
    }
    if ('keyword' in parsed) {
        return parsed.keyword;
    }
    return null;
}
